import {env, sentryDsn} from "../constants";
import {getUrlInfo, Urls} from "../helpers";
import {useAuth} from "./auth/AuthContext";
import * as Sentry from "@sentry/react";
import {setTag} from "@sentry/react";
import React, {createContext, PropsWithChildren, useCallback, useEffect} from "react";
import {createRoutesFromChildren, matchRoutes, useLocation, useNavigationType} from "react-router-dom";

export interface SentryContextProps {
  setTag: typeof setTag;
}

const SentryContext = createContext<SentryContextProps>(undefined!);

interface IProps extends PropsWithChildren {
  urls: Urls;
}

let sentryInitialized = false;

export const SentryProvider = ({urls, children}: IProps) => {
  const {userId, isAgent} = useAuth();
  const {pathname, search} = useLocation();
  const queryParams = new URLSearchParams(search);
  const urlUserId = queryParams.get("id");
  const enabled = ["production", "staging"].includes(env);

  useEffect(() => {
    if (enabled && !sentryInitialized) {
      Sentry.init({
        enabled: true,
        dsn: sentryDsn,
        environment: env,
        integrations: [
          Sentry.reactRouterV6BrowserTracingIntegration({
            useEffect: React.useEffect,
            useLocation,
            useNavigationType,
            createRoutesFromChildren,
            matchRoutes,
          }),
          Sentry.replayIntegration({
            maskAllText: false,
            blockAllMedia: false,
          }),
        ],
        beforeSendTransaction: (event) => {
          if (event.spans) {
            event.spans = event.spans.filter((span) => !span.op?.startsWith("resource."));
          }
          return event;
        },
        tracesSampleRate: 0.2,
        replaysSessionSampleRate: isAgent ? 0 : 1,
        replaysOnErrorSampleRate: 1.0,
      });
      sentryInitialized = true;
    }
  }, [enabled, isAgent]);

  const identifyUser = useCallback((userId?: string): void => {
    if (userId) {
      Sentry.setUser({id: userId});
    } else {
      Sentry.setUser({});
    }
  }, []);

  useEffect(() => {
    if (userId) {
      identifyUser(userId);
    } else if (urlUserId) {
      identifyUser(urlUserId);
    }
  }, [userId, urlUserId]);

  useEffect(() => {
    if (!pathname) {
      return;
    }

    const urlInfo = getUrlInfo(urls, pathname);
    if (!urlInfo) {
      return;
    }
  }, [pathname]);

  return <SentryContext.Provider value={{setTag}}>{children}</SentryContext.Provider>;
};
